/**
 * 通用表格:支持点击列头排序
 * - 排序在前端做(不重新查后端)
 * - action 列(查看链接等)用 sortable: false
 * - 数字列(mono)默认 desc,文本列默认 asc
 */
import { useState } from 'react'

export type SortDir = 'asc' | 'desc'
export interface ColDef {
  k: string
  label: string
  mono?: boolean
  render?: (r: any) => React.ReactNode
  sortable?: boolean
}

interface Props {
  rows: any[]
  cols: ColDef[]
  onRowClick?: (row: any) => void
}

export function Table({ rows, cols, onRowClick }: Props) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null)

  if (!rows.length) return <div style={{ color: 'var(--ink3)', fontSize: 'var(--fs-capt)' }}>没找到数据。试试调整年份 / 科类 / 批次?</div>

  function handleSort(col: ColDef) {
    if (col.sortable === false) return
    setSort(prev => {
      if (prev?.key === col.k) {
        // 同列 → 翻转方向
        return { key: col.k, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      }
      // 新列 → 数字列默认 desc,文本列默认 asc
      return { key: col.k, dir: col.mono ? 'desc' : 'asc' }
    })
  }

  const sorted = sort
    ? [...rows].sort((a, b) => {
        const av = a[sort.key]
        const bv = b[sort.key]
        if (av == null && bv == null) return 0
        if (av == null) return 1  // null 排最后
        if (bv == null) return -1
        let cmp: number
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv
        } else {
          cmp = String(av).localeCompare(String(bv), 'zh-Hans-CN', { numeric: true })
        }
        return sort.dir === 'asc' ? cmp : -cmp
      })
    : rows

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="tbl">
        <thead>
          <tr>
            {cols.map(c => {
              const sortable = c.sortable !== false
              const isActive = sort?.key === c.k
              return (
                <th
                  key={c.k}
                  onClick={sortable ? () => handleSort(c) : undefined}
                  style={{
                    cursor: sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  title={sortable ? '点击排序' : undefined}
                >
                  {c.label}
                  {isActive && <span style={{ marginLeft: 4, opacity: 0.7 }}>{sort!.dir === 'asc' ? '↑' : '↓'}</span>}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr
              key={i}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {cols.map(c => (
                <td key={c.k} className={c.mono ? 'num' : ''}>
                  {c.render ? c.render(r) : String(r[c.k] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
